import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "name", "placeholder", "size", "options", "value", "defaultValue", "onChange", "disabled", "readonly", "searchable", "showClearButton", "onKeyDown", "validationState", "search", "autocomplete", "prefixIcon", "prefix", "postfix", "removeByBackspace", "addOptionByEnter", "open", "onOpenChange", "selectedOptionFormatter"],
  _excluded2 = ["content"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import cn from 'classnames';
import mergeRefs from 'merge-refs';
import { forwardRef, useMemo, useRef, useState } from 'react';
import { useLocale } from '@sbercloud/uikit-product-locale';
import { MobileModalCustom } from '@sbercloud/uikit-product-mobile-modal';
import { ButtonFilled, ButtonFunction } from '@snack-uikit/button';
import { FieldDecorator } from '@snack-uikit/fields';
import { InputPrivate } from '@snack-uikit/input-private';
import { kindFlattenItems, List } from '@snack-uikit/list';
import { Tag } from '@snack-uikit/tag';
import { extractSupportProps, isBrowser, useLayoutEffect, useValueControl } from '@snack-uikit/utils';
import { FieldContainerPrivate, ItemContent } from '../../helperComponents';
import { usePostfix, usePrefix } from '../../hooks';
import { useButtons, useHandleDeleteItem, useHandleOnKeyDown, useSearchInput } from './hooks';
import { useFuzzySearch } from './legacy';
import styles from "./styles.module.css";
import { extractFieldDecoratorProps, extractListProps, getArrowIcon, getValidationState, updateMultipleItems } from './utils';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var defaultSelectedOptionFormatter = function defaultSelectedOptionFormatter(item) {
  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (item === null || item === void 0 ? void 0 : item.content.option) || ''
  );
};
export var MobileFieldSelectMultiple = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var id = _ref.id,
    name = _ref.name,
    placeholder = _ref.placeholder,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 's' : _ref$size,
    options = _ref.options,
    valueProp = _ref.value,
    defaultValue = _ref.defaultValue,
    onChangeProp = _ref.onChange,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$readonly = _ref.readonly,
    readonly = _ref$readonly === void 0 ? false : _ref$readonly,
    _ref$searchable = _ref.searchable,
    searchableProp = _ref$searchable === void 0 ? true : _ref$searchable,
    _ref$showClearButton = _ref.showClearButton,
    showClearButton = _ref$showClearButton === void 0 ? true : _ref$showClearButton,
    onInputKeyDownProp = _ref.onKeyDown,
    _ref$validationState = _ref.validationState,
    validationState = _ref$validationState === void 0 ? 'default' : _ref$validationState,
    search = _ref.search,
    _ref$autocomplete = _ref.autocomplete,
    autocomplete = _ref$autocomplete === void 0 ? false : _ref$autocomplete,
    prefixIcon = _ref.prefixIcon,
    prefix = _ref.prefix,
    postfix = _ref.postfix,
    _ref$removeByBackspac = _ref.removeByBackspace,
    removeByBackspace = _ref$removeByBackspac === void 0 ? false : _ref$removeByBackspac,
    _ref$addOptionByEnter = _ref.addOptionByEnter,
    addOptionByEnter = _ref$addOptionByEnter === void 0 ? false : _ref$addOptionByEnter,
    openProp = _ref.open,
    onOpenChange = _ref.onOpenChange,
    _ref$selectedOptionFo = _ref.selectedOptionFormatter,
    selectedOptionFormatter = _ref$selectedOptionFo === void 0 ? defaultSelectedOptionFormatter : _ref$selectedOptionFo,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useLocale = useLocale('MobileFields'),
    t = _useLocale.t;
  var localRef = useRef(null);
  var contentRef = useRef(null);
  var _useValueControl = useValueControl({
      value: openProp,
      onChange: onOpenChange
    }),
    _useValueControl2 = _slicedToArray(_useValueControl, 2),
    _useValueControl2$ = _useValueControl2[0],
    open = _useValueControl2$ === void 0 ? false : _useValueControl2$,
    setOpen = _useValueControl2[1];
  var _useValueControl3 = useValueControl({
      value: valueProp,
      defaultValue: defaultValue,
      onChange: onChangeProp
    }),
    _useValueControl4 = _slicedToArray(_useValueControl3, 2),
    value = _useValueControl4[0],
    setValue = _useValueControl4[1];
  var _useState = useState(function () {
      return updateMultipleItems({
        options: options,
        value: value,
        currentItems: [],
        selectedItems: undefined
      });
    }),
    _useState2 = _slicedToArray(_useState, 2),
    _useState2$ = _useState2[0],
    selectedItems = _useState2$.selectedItems,
    _useState2$$items = _useState2$.items,
    items = _useState2$$items === void 0 ? [] : _useState2$$items,
    setItems = _useState2[1];
  var _useMemo = useMemo(function () {
      return kindFlattenItems({
        items: items
      });
    }, [items]),
    flattenItems = _useMemo.flattenItems;
  var searchable = searchableProp && Object.values(flattenItems).length > 5 || autocomplete;
  var _useSearchInput = useSearchInput(_objectSpread(_objectSpread({}, search), {}, {
      defaultValue: '',
      selectedOptionFormatter: selectedOptionFormatter
    })),
    inputValue = _useSearchInput.inputValue,
    setInputValue = _useSearchInput.setInputValue,
    prevInputValue = _useSearchInput.prevInputValue,
    updateInputValue = _useSearchInput.updateInputValue;
  var prefixSettings = usePrefix({
    prefix: prefix,
    disabled: disabled
  });
  var postfixSettings = usePostfix({
    postfix: postfix,
    disabled: disabled
  });
  useLayoutEffect(function () {
    setItems(function (_ref2) {
      var selectedItems = _ref2.selectedItems;
      return updateMultipleItems({
        options: options,
        value: value,
        selectedItems: selectedItems
      });
    });
  }, [options, value]);
  var onClear = function onClear() {
    var _localRef$current;
    setValue(selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.filter(function (item) {
      return item.disabled;
    }).map(function (item) {
      return item.id;
    }));
    (_localRef$current = localRef.current) === null || _localRef$current === void 0 ? void 0 : _localRef$current.focus();
    if (rest.required) {
      setOpen(true);
    }
  };
  var _getArrowIcon = getArrowIcon({
      size: size,
      open: open
    }),
    ArrowIcon = _getArrowIcon.ArrowIcon,
    arrowIconSize = _getArrowIcon.arrowIconSize;
  var _useButtons = useButtons({
      readonly: readonly,
      size: size,
      showClearButton: showClearButton && !disabled && !readonly && Boolean(selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.find(function (item) {
        return !item.disabled;
      })),
      showCopyButton: false,
      inputRef: localRef,
      onClear: onClear
    }),
    postfixButtons = _useButtons.postfixButtons,
    inputKeyDownNavigationHandler = _useButtons.inputKeyDownNavigationHandler,
    buttonsRefs = _useButtons.buttonsRefs;
  var commonHandleOnKeyDown = useHandleOnKeyDown({
    inputKeyDownNavigationHandler: inputKeyDownNavigationHandler,
    onInputKeyDownProp: onInputKeyDownProp,
    setOpen: setOpen
  });
  var handleItemDelete = useHandleDeleteItem(setValue);
  var handleOnKeyDown = function handleOnKeyDown(onKeyDown) {
    return function (e) {
      if (removeByBackspace && e.code === 'Backspace' && inputValue === '') {
        if (selectedItems !== null && selectedItems !== void 0 && selectedItems.length && !selectedItems.slice(-1)[0].disabled) {
          handleItemDelete(selectedItems.pop())();
        }
      }
      if (e.code === 'Enter') {
        e.stopPropagation();
        e.preventDefault();
      }
      if (addOptionByEnter && e.code === 'Enter' && inputValue !== '') {
        if (!(value !== null && value !== void 0 ? value : []).includes(inputValue)) {
          setValue(function (value) {
            return (value !== null && value !== void 0 ? value : []).concat(inputValue);
          });
          updateInputValue();
        }
      }
      if (!open && prevInputValue.current !== inputValue) {
        setOpen(true);
      }
      commonHandleOnKeyDown(onKeyDown)(e);
    };
  };
  var handleOpenChange = function handleOpenChange(open) {
    if (!readonly && !disabled && isBrowser() && !buttonsRefs.includes(document.activeElement)) {
      setOpen(open);
      if (!open) {
        updateInputValue();
      }
    }
  };
  var handleApplyChange = function handleApplyChange() {
    onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.map(function (item) {
      return item.id;
    }));
    handleOpenChange(false);
    setInputValue('');
  };
  var handleBlur = function handleBlur(e) {
    if (!open && !buttonsRefs.filter(Boolean).includes(e.relatedTarget)) {
      var _rest$onBlur;
      updateInputValue();
      rest === null || rest === void 0 ? void 0 : (_rest$onBlur = rest.onBlur) === null || _rest$onBlur === void 0 ? void 0 : _rest$onBlur.call(rest, e);
    }
  };
  var fuzzySearch = useFuzzySearch(items);
  var result = autocomplete || !searchable || prevInputValue.current === inputValue ? items : fuzzySearch(inputValue);
  var fieldValidationState = getValidationState({
    validationState: validationState,
    error: rest.error
  });
  var listJsx = /*#__PURE__*/_jsx("div", {
    className: styles.listWrapper,
    children: /*#__PURE__*/_jsx(List, _objectSpread(_objectSpread({}, extractListProps(rest)), {}, {
      items: result,
      size: "l",
      scroll: true,
      search: searchable ? {
        value: inputValue,
        onChange: setInputValue
      } : undefined,
      contentRender: function contentRender(_ref3) {
        var content = _ref3.content,
          rest = _objectWithoutProperties(_ref3, _excluded2);
        if (typeof content !== 'function') {
          return /*#__PURE__*/_jsx(ItemContent, _objectSpread(_objectSpread({}, content), rest));
        }
        return content;
      },
      selection: {
        mode: 'multiple',
        value: value,
        onChange: function onChange(value) {
          setValue(value);
        }
      }
    }))
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FieldDecorator, _objectSpread(_objectSpread(_objectSpread({}, extractSupportProps(rest)), extractFieldDecoratorProps(rest)), {}, {
      labelFor: id,
      size: size,
      validationState: fieldValidationState,
      children: /*#__PURE__*/_jsx(FieldContainerPrivate, {
        className: cn(styles.container, styles.tagContainer),
        validationState: fieldValidationState,
        disabled: disabled,
        readonly: readonly,
        focused: open,
        variant: "single-line-container",
        inputRef: localRef,
        size: size,
        prefix: (prefixIcon || prefixSettings.show) && /*#__PURE__*/_jsxs(_Fragment, {
          children: [prefixIcon, prefixSettings.show && prefixSettings.render({
            key: prefixSettings.id
          })]
        }),
        onClick: function onClick() {
          handleOpenChange(true);
        },
        children: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs("div", {
            className: styles.contentWrapper,
            ref: contentRef,
            children: [selectedItems && selectedItems.map(function (option) {
              var _option$appearance;
              return /*#__PURE__*/_jsx(Tag, {
                size: size === 'l' ? 's' : 'xs',
                tabIndex: -1,
                label: selectedOptionFormatter(option),
                appearance: (_option$appearance = option.appearance) !== null && _option$appearance !== void 0 ? _option$appearance : 'neutral'
              }, option.id);
            }), /*#__PURE__*/_jsx("div", {
              className: styles.inputWrapper,
              children: /*#__PURE__*/_jsx(InputPrivate, {
                id: id,
                name: name,
                type: "text",
                disabled: disabled,
                placeholder: !selectedItems || !selectedItems.length ? placeholder : undefined,
                ref: mergeRefs(ref, localRef),
                onChange: undefined,
                value: '',
                readonly: true,
                "data-test-id": "field-select__input",
                onKeyDown: handleOnKeyDown(),
                onBlur: handleBlur,
                className: styles.readonlyCursor
              })
            })]
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.postfix,
            children: [postfixButtons, postfixSettings.show && postfixSettings.render({
              key: postfixSettings.id
            }), /*#__PURE__*/_jsx(ArrowIcon, {
              size: arrowIconSize,
              className: styles.arrowIcon
            })]
          })]
        })
      })
    })), /*#__PURE__*/_jsxs(MobileModalCustom, {
      open: open,
      onClose: function onClose() {
        return handleOpenChange(false);
      },
      size: searchable ? 'full' : 'auto',
      children: [rest.label && /*#__PURE__*/_jsx(MobileModalCustom.Header, {
        title: rest.label
      }), searchable ? listJsx : /*#__PURE__*/_jsx(MobileModalCustom.Body, {
        className: styles.bodyNoPadding,
        content: listJsx
      }), /*#__PURE__*/_jsx(MobileModalCustom.Footer, {
        actions: /*#__PURE__*/_jsxs("div", {
          className: styles.footer,
          children: [/*#__PURE__*/_jsxs("div", {
            className: styles.footerTopLine,
            children: [/*#__PURE__*/_jsx("span", {
              className: styles.counter,
              children: "".concat(t('selectedN')).concat((selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length) || 0)
            }), /*#__PURE__*/_jsx(ButtonFunction, {
              label: t('resetAll'),
              onClick: onClear,
              size: "m"
            })]
          }), /*#__PURE__*/_jsx(ButtonFilled, {
            fullWidth: true,
            label: t('select'),
            onClick: handleApplyChange,
            size: "l"
          })]
        })
      })]
    })]
  });
});